import {useNotificacao} from "@components/usuario/notificacoes/hooks/useNotificacao.ts";
import {AlertContext} from "@contexts/hooks/useAlert.ts";
import {IOperationResponse, type OperationResponseStatus} from "@localization/siteMap/siteMapInterfaces.ts";
import '@css/toastifyOverrides.css';
import type {INotificacao} from "@models/conta/notificacao.js";
import {Alert, Box, Fade, Grid, useColorScheme} from "@mui/material";
import {PopUp, PopUpProps} from "@shared/PopUp.tsx";
import React, {memo, ReactElement, useCallback, useEffect, useState} from "react";
import {toast, ToastContainer} from "react-toastify";
import {uuidv7} from 'uuidv7';

export interface AlertContextType {
	createAlert: (message: string | undefined, severity: severity, time: number) => void
	createAlertFromResponse: (response: IOperationResponse<any>) => void
	getNotificacoes: () => IOperationResponse<INotificacao[]> | undefined,
	addPopUp: (props: Pick<PopUpProps, 'size' | 'content' | 'title'>) => string
	closePopUp: (filt?: { index?: number, uid?: string, closeAll?: boolean }) => void
}

export type severity = 'error' | 'warning' | 'info' | 'success'

const WarnsComponent = memo((props: { warns: { message: string, status: severity, liveTime: number }[] }) => {
	const {warns} = props;
	return <Grid container flexDirection={'column'} spacing={1}>{(warns.slice(0, 5).map((x, i) => <Warn {...x}
	                                                                                                    key={i}/>))}</Grid>
}, (prev, next) => prev.warns.length == next.warns.length && prev.warns.every((x, i) => x.message == next.warns[i].message && x.status == next.warns[i].status))

const Warn = memo((props: { message: string, status: severity, liveTime: number }) => {
	const [open, setOpen] = useState(true);
	useEffect(() => {
		setTimeout(() => {
			setOpen(false)
		}, props.liveTime)
	}, [])

	return (<Fade in={open} timeout={250}>
		<Alert sx={{mb: 1}} title={props.message}
		       severity={props.status}>{props.message}</Alert>
	</Fade>)
})

export const AlertProvider: React.FC<{ children: ReactElement }> = ({children}) => {
	const {mode} = useColorScheme();
	const {getNotificacoes: getNotificacoesHook} = useNotificacao()
	const [popUps, setPopUps] = useState<PopUpProps[]>([]);
	const [notificacoes, setNotificacoes] = useState<IOperationResponse<INotificacao[]>>();
	const [warns, setWarns] = useState<{ id: string, message: string, status: severity, liveTime: number }[]>([])


	const addPopUp = (props: Pick<PopUpProps, 'size' | 'content' | 'title'>): string => {
		const uid = uuidv7();
		setPopUps((prev) => [...prev, {
			...props, size: props.size ?? 'max', open: true, setOpen: () => {
			},
			uid,
		}]);

		return uid;
	}

	const createAlert = (message: string | undefined, severity: severity, time: number = 3000) => {
		if (!message) message = '';
		const alertOpts = {autoClose: time};

		const alFn = {
			'error': () => toast.error(message, {...alertOpts}),
			'warning': () => toast.warning(message, {...alertOpts}),
			'info': () => toast.info(message, {...alertOpts}),
			'success': () => toast.success(message, {...alertOpts}),
		}[severity]

		return alFn()
	};

	const createAlertFromResponse = (response: IOperationResponse<any>) => {
		const {notifications} = response
		const livetime = 6000

		// Cria as mensagens fora a mensagem principal, 1 segundo de delay entre o tempo de vida da mensagem para ter o efeito de fade
		if (notifications != null) {
			const id = uuidv7()
			setWarns([...warns, ...notifications.map(x => {
				setTimeout(() => {
					setWarns(prev => [...prev.filter(y =>
						id != y.id)])
				}, livetime + 200)

				return {message: x.message, status: x.status, liveTime: livetime, id: id}
			})])
		}

		createAlert(response.message, normalizeSeverity(response.status));
	};


	const normalizeSeverity = (status: OperationResponseStatus) => {
		return ({
			'Success': 'success',
			'Error': 'error',
			'Warning': 'warning',
			'Info': 'info',
			'Unauthorized': 'error',
			'NotFound': 'error',
			'BadRequest': 'error',
			'Conflict': 'error',
			'Forbidden': 'error',
			'MethodNotAllowed': 'error',
			'None': 'info',
		}[status] || 'info') as severity
	};

	const getNotificacoes = useCallback(async () => {
		return getNotificacoesHook(true, {page: 1, pageSize: 10})
			.then(x => {
				if (x.status == 'Success')
					setNotificacoes(x)
			})
			.catch(error => {
				console.error('Failed to fetch notifications via hook:', error);
			})
	}, [getNotificacoesHook]);

	useEffect(() => {
		getNotificacoes().finally(() => {
			setTimeout(getNotificacoes, 10000)
		});
	}, []);

	const closePopUp = (f?: { index?: number, uid?: string, closeAll?: boolean }) => {
		if (f?.closeAll) {
			setPopUps([])
			return
		}

		if (!f?.index && !f?.uid) {
			setPopUps(popUps.filter((_p, i) => i !== popUps.length));
			return;
		}

		const index = f?.index ?? popUps.findIndex((p) => p.uid === f?.uid);
		if (index === -1) {
			console.warn('PopUp not found to close');
			return;
		}
		setPopUps((prev) => prev.filter((_, i) => i !== index));
	};

	return (
		<AlertContext.Provider
			value={{
				createAlert,
				createAlertFromResponse,
				getNotificacoes: () => notificacoes,
				addPopUp,
				closePopUp
			}}>
			<Box position={'fixed'} sx={{top: 5, right: 5, zIndex: 10000}}>
				<WarnsComponent warns={warns}/>
			</Box>
			{children}
			<ToastContainer
				position="bottom-center"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={false}
				stacked
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				theme={mode == 'light' ? 'light' : 'dark'}
				draggable
				pauseOnHover
			/>
			{popUps.map((popUp) => (
				<PopUp key={popUp.uid}
				       title={popUp.title}
				       content={popUp.content}
				       open={popUp.open}
				       setOpen={popUp.setOpen}
				       onClose={() => {
					       if (popUp.uid)
						       closePopUp({uid: popUp.uid});
				       }}
				       size={popUp.size || 'md'}
				       uid={popUp.uid}
				/>

			))}
		</AlertContext.Provider>
	);
};

